import {bool, number, string, oneOf} from 'prop-types';
import {remap} from '@sinclair-digital/sbg-util/dist/es/object';
import {anyPass, assoc, dissoc, merge, pipe, propEq, reduce, reject} from 'ramda';
import {tagEquals, TagModel} from './BaseTagModel';
import {SOURCE_CALAIS, SOURCE_USERGENERATED, SOURCE_AI} from '../../constants/tags';

export {tagEquals, TagModel as default};

export const tagCategories = [
    'City',
    'Company',
    'Country',
    'Entertainment',
    'Event',
    'HashTag',
    'Organization',
    'Person',
    'Place',
    'Product',
    'Region',
    'Social',
    'Technology',
    'Topic',
];

// Tag ::
export const tagShape = {
    uuid: string.isRequired,
    removable: bool.isRequired,
    name: string.isRequired,
    dimension: string.isRequired,
    weight: number.isRequired,
    userGenerated: bool,
    included: bool,
    source: oneOf([SOURCE_USERGENERATED, SOURCE_AI, SOURCE_CALAIS]),
};

export const partialTagShapeV2 = {
    excluded: bool.isRequired,
    source: string.isRequired,
    weight: number.isRequired,
    uuid: string,
};

export const tagShapeV2 = {
    ...partialTagShapeV2,
    name: string.isRequired,
    dimension: string.isRequired,
};

export const tagSearchV2ResponseShape = {
    tagUuid: string.isRequired,
    tagName: string.isRequired,
    dimensionUuid: string.isRequired,
    dimensionName: string.isRequired,
    dimensionTypeUuid: string.isRequired,
    dimensionTypeName: string.isRequired,
    type: string.isRequired,
};

export const tagV2toV1 = ({excluded, ...rest}) => ({included: !excluded, ...rest, removable: true});

// doesn't have package-specific fields
export const tagSearchV2ToV2 = remap({tagName: 'name', dimensionName: 'dimension', tagUuid: 'uuid'});

export const tagComparitor = (a, b) => {
    const aUserGenerated = a.userGenerated === true;
    const bUserGenerated = b.userGenerated === true;
    const bothUserGenerated = aUserGenerated && bUserGenerated;
    const sortByName = (nameA, nameB) => nameA.localeCompare(nameB);
    const sortByDimension = (dimA, dimB) => dimA.localeCompare(dimB);
    if (bothUserGenerated) {
        const dimGroup = sortByDimension(a.dimension, b.dimension);
        return dimGroup === 0 ? sortByName(a.name, b.name) : dimGroup;
    } else if (aUserGenerated) {
        return -1;
    } else if (bUserGenerated) {
        return 1;
    } else {
        if (a.weight === b.weight) {
            const dimGroup = sortByDimension(a.dimension, b.dimension);
            return dimGroup === 0 ? sortByName(a.name, b.name) : dimGroup;
        }
        return a.weight < b.weight ? 1 : -1;
    }
};

const createTargetTag = uuid => ({uuid, source: 'Target', excluded: false, weight: 1});

export const createTargetTags = ({defaultTagUuids, sectionTagUuids = []}) =>
    defaultTagUuids
        .map(createTargetTag)
        .concat(sectionTagUuids.map(createTargetTag));

const tagSourceEquals = propEq('source');

// isTargetTag :: Tag -> Boolean
const isTargetTag = tagSourceEquals('Target');

// isRestrictionTag :: Tag -> Boolean
export const isRestrictionTag = tagSourceEquals('Restriction');

// isDMATag :: Tag -> Boolean
export const isDmaTag = tagSourceEquals('DMA');

// isStationAttributionTag :: Tag -> Boolean
export const isStationAttributionTag = tagSourceEquals('StationAttribution');

// tagsToTagMap :: [Tag] -> TagMap
const tagsToTagMap = reduce((tagMap, tag) => assoc(tag.uuid, dissoc('uuid', tag), tagMap), {});

// setTargetTags :: [Tag] -> TagMap -> TagMap
export const setTargetTags = targetTags =>
    pipe(
        reject(isTargetTag),
        merge(tagsToTagMap(targetTags)),
    );

// setDMATags :: TagMap -> TagMap -> TagMap
export const setDmaTags = newMap => tagMap =>
    pipe(
        reject(anyPass([isRestrictionTag, isDmaTag, isStationAttributionTag])),
        merge(newMap),
    )(tagMap);

export const getTagSummaryFromTagMapByDimension = (tagMap, providers, dimension) =>
    Object.keys(tagMap || {})
        .filter(uuid => tagMap[uuid].source === dimension)
        .reduce((obj, uuid) => providers.find(p => p.tagUuid === uuid), {});


export const tagSummariesToTagMap = tagSummaries => {
    return tagSummaries
        .filter(t => t.tagUuid && t.dimensionName)
        .reduce((obj, tag) => (obj[tag.tagUuid] = {
            source: tag.dimensionName,
            weight: 1,
            excluded: false,
        }, obj), {});
};

export const setTagMapFromTagSummaries = (tagMap, ...tagSummaries) => {
    const dimensionRejectSet = new Set(tagSummaries.map(t => t.dimensionName));
    const filteredTagMap = Object.keys(tagMap || {})
        .filter(uuid => !dimensionRejectSet.has(tagMap[uuid].source))
        .reduce((obj, uuid) => ((obj[uuid] = tagMap[uuid]), obj), {});
    return merge(filteredTagMap, tagSummariesToTagMap(tagSummaries));
};
